/**
 * PasswordResetConfirmForm Component
 *
 * Password reset confirmation form with token validation and new password creation.
 * Handles the final step of the password reset workflow when users click the reset link.
 */

// 1. External dependencies
import React, { useState, useCallback } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle } from 'lucide-react';

// 2. Internal absolute imports
import { Button, Input, Form, Alert, Link, Card, CardBody, CardFooter } from '@enfomentis/ui';
import { AppLayout } from '@/components/shared/layouts';
import { useAuth } from '@/hooks/useAuth';
import type { AuthErrorCode } from '@/types/auth';

/**
 * Password Reset Confirmation Component
 *
 * Features:
 * - Password creation form with validation and show/hide functionality
 * - Confirm password matching validation
 * - Token validation happens server-side via Edge Function
 * - Error states for expired/invalid/missing tokens (from server)
 * - Success state with redirect to login
 * - Mobile-responsive design following established auth component patterns
 * - Security best practices with server-side token validation
 */
export function PasswordResetConfirmForm(): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const { confirmPasswordReset } = useAuth();

  // Extract token from URL query parameter
  const token = searchParams.get('token');

  // Form state
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);

  // Password reset success state
  const [isResetComplete, setIsResetComplete] = useState(false);

  // Toggle password visibility
  const togglePasswordVisibility = useCallback(() => {
    setIsPasswordVisible(prev => !prev);
  }, []);

  const toggleConfirmPasswordVisibility = useCallback(() => {
    setIsConfirmPasswordVisible(prev => !prev);
  }, []);

  // Password validation function
  const validatePassword = (value: string): string | null => {
    if (!value) return "Password is required";
    if (value.length < 8) return "Password must be at least 8 characters long";
    if (!/(?=.*[a-z])/.test(value)) return "Password must contain at least one lowercase letter";
    if (!/(?=.*[A-Z])/.test(value)) return "Password must contain at least one uppercase letter";
    if (!/(?=.*\d)/.test(value)) return "Password must contain at least one number";
    return null;
  };

  // Confirm password validation function
  const validateConfirmPassword = (value: string): string | null => {
    if (!value) return "Please confirm your password";
    if (value !== formData.newPassword) return "Passwords do not match";
    return null;
  };

  // Validation state - show errors after submit
  const passwordError = hasSubmitted ? validatePassword(formData.newPassword) : null;
  const confirmPasswordError = hasSubmitted ? validateConfirmPassword(formData.confirmPassword) : null;

  // Form submission handler
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setHasSubmitted(true);

    // Client-side validation before submission
    const newPasswordError = validatePassword(formData.newPassword);
    const confirmPasswordError = validateConfirmPassword(formData.confirmPassword);

    if (newPasswordError || confirmPasswordError) {
      return; // Don't submit if there are validation errors
    }

    setIsSubmitting(true);
    setServerErrors({});

    try {
      const result = await confirmPasswordReset({
        resetToken: token || '',
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword
      });

      if (result.success) {
        setIsResetComplete(true);
      } else {
        // Map authentication errors to form field errors
        const errors: Record<string, string> = {};

        switch (result.error as AuthErrorCode) {
          case 'VALIDATION_ERROR':
            errors['general'] = result.message || 'Please check your password requirements';
            break;
          case 'RESET_TOKEN_EXPIRED':
            errors['general'] = 'This password reset link has expired. Please request a new one';
            break;
          case 'INVALID_RESET_TOKEN':
            errors['general'] = 'This password reset link is invalid. Please request a new one';
            break;
          case 'RATE_LIMITED':
            errors['general'] = 'Too many password reset attempts. Please try again later';
            break;
          default:
            errors['general'] = result.message || 'Password reset failed. Please try again';
        }

        setServerErrors(errors);
      }
    } catch (error) {
      console.error('❌ PasswordResetConfirmForm: Password reset error:', error);
      setServerErrors({ 'general': 'Network error occurred. Please check your connection and try again' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Password reset success state
  if (isResetComplete) {
    return (
      <AppLayout hideFooter={true}>
        <Card className="items-center justify-center h-full" radius="none" shadow="none">
          <CardBody className="flex max-w-md mx-auto p-6 mt-10">
            <div className="text-center space-y-6">
              <CheckCircle className="w-12 h-12 text-success-500 mx-auto" />
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-default-900">
                  Password Reset Successful
                </h3>
                <p className="text-default-600">
                  Your password has been successfully reset. You can now sign in with your new password.
                </p>
              </div>
              <div className="space-y-4">
                <RouterLink to="/login">
                  <Button
                    color="primary"
                    size="lg"
                    className="w-full"
                  >
                    Sign In
                  </Button>
                </RouterLink>
              </div>
            </div>
          </CardBody>
        </Card>
      </AppLayout>
    );
  }

  // Password reset form state
  return (
    <AppLayout hideFooter={true}>
      <Card className="items-center justify-center h-full" radius="none" shadow="none">
        <CardBody className="flex max-w-md mx-auto p-6 mt-10">
          <Form
            validationBehavior="aria"
            validationErrors={serverErrors}
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            <div className="text-center mb-6">
              <h3 className="text-lg font-medium text-default-900 mb-2">
                Create New Password
              </h3>
              <p className="text-default-600">
                Enter your new password below. Make sure it's strong and secure.
              </p>
            </div>

            {/* New Password Input */}
            <Input
              type={isPasswordVisible ? "text" : "password"}
              label="New Password"
              placeholder="Enter your new password"
              value={formData.newPassword}
              onValueChange={(value) => setFormData(prev => ({ ...prev, newPassword: value }))}
              validate={validatePassword}
              isRequired
              autoComplete="new-password"
              errorMessage={serverErrors['newPassword'] || passwordError}
              isInvalid={!!serverErrors['newPassword'] || !!passwordError}
              endContent={
                <button
                  className="focus:outline-none"
                  type="button"
                  onClick={togglePasswordVisibility}
                  aria-label={isPasswordVisible ? "Hide password" : "Show password"}
                >
                  {isPasswordVisible ? (
                    <Eye className="w-4 h-4 text-default-400 pointer-events-none" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-default-400 pointer-events-none" />
                  )}
                </button>
              }
              classNames={{
                input: "text-base", // 16px font size to prevent mobile zoom
              }}
            />

            {/* Password Requirements */}
            <div className="text-xs text-default-500 space-y-1">
              <p>Password requirements:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>At least 8 characters long</li>
                <li>Contains uppercase and lowercase letters</li>
                <li>Contains at least one number</li>
              </ul>
            </div>

            {/* Confirm Password Input */}
            <Input
              type={isConfirmPasswordVisible ? "text" : "password"}
              label="Confirm Password"
              placeholder="Confirm your new password"
              value={formData.confirmPassword}
              onValueChange={(value) => setFormData(prev => ({ ...prev, confirmPassword: value }))}
              validate={validateConfirmPassword}
              isRequired
              autoComplete="new-password"
              errorMessage={serverErrors['confirmPassword'] || confirmPasswordError}
              isInvalid={!!serverErrors['confirmPassword'] || !!confirmPasswordError}
              endContent={
                <button
                  className="focus:outline-none"
                  type="button"
                  onClick={toggleConfirmPasswordVisibility}
                  aria-label={isConfirmPasswordVisible ? "Hide password" : "Show password"}
                >
                  {isConfirmPasswordVisible ? (
                    <Eye className="w-4 h-4 text-default-400 pointer-events-none" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-default-400 pointer-events-none" />
                  )}
                </button>
              }
              classNames={{
                input: "text-base", // 16px font size to prevent mobile zoom
              }}
            />

            {/* General Error Display */}
            {serverErrors['general'] && (
              <Alert
                color="warning"
                variant="flat"
                description={serverErrors['general']}
              />
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              color="primary"
              size="lg"
              isLoading={isSubmitting}
              className="w-full"
            >
              {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
            </Button>
          </Form>
        </CardBody>

        {/* Auth Navigation Links */}
        <CardFooter className="max-w-md mx-auto justify-center border-t border-default-100">
          <div className="text-center">
            <p className="text-sm text-text-secondary">
              Remember your password?{' '}
              <Link as={RouterLink} {...({ to: "/login" } as { to: string })} color="primary" size="sm">
                Sign in here
              </Link>
            </p>
            <p className="text-sm">
              <Link as={RouterLink} {...({ to: "/password-reset" } as { to: string })} color="foreground" size="sm">
                Request new reset link
              </Link>
            </p>
          </div>
        </CardFooter>
      </Card>
    </AppLayout>
  );
}